import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import './ProductsPage.css';
import { bombWorldClassicConfig, infinityBrainConfig, personalContactConfig, personalPrizeConfig, personalVaultConfig, wayHomeConfig } from '../config/products';

const ProductsPage: React.FC = () => {
  const { t } = useTranslation();
  const [comingSoonLink, setComingSoonLink] = useState<string | null>(null);

  const showComingSoon = (name: string) => {
    setComingSoonLink(name);
    window.setTimeout(() => {
      setComingSoonLink((current) => (current === name ? null : current));
    }, 2500);
  };

  const products = [
    {
      id: 'bomb-world-classic',
      route: '/bomb-world-classic',
      logo: bombWorldClassicConfig.logo,
      name: t('products.bombWorldClassic.name', 'Bomb World: Classic'),
      subtitle: t('products.bombWorldClassic.subtitle', 'Classic Bomb Island feel — solo, endless, and LAN multiplayer'),
      description: (() => {
        const paragraphs = t('products.bombWorldClassic.aboutParagraphs', { returnObjects: true });
        if (Array.isArray(paragraphs) && paragraphs[0]) {
          const text = paragraphs[0].replace(/<[^>]*>/g, '');
          return text.length > 150 ? text.substring(0, 150) + '...' : text;
        }
        return 'Bomb World: Classic is a next-generation remake of classic Bomb Island.';
      })(),
      features: (() => {
        const featuresResult = t('products.bombWorldClassic.features', { returnObjects: true });
        return Array.isArray(featuresResult)
          ? (featuresResult as Array<{ emoji: string; title: string; description: string }>).slice(0, 4)
          : [];
      })(),
      gradientColors: bombWorldClassicConfig.gradientColors,
      comingSoonPlayStore: true,
      appStoreUrl: bombWorldClassicConfig.appStoreUrl,
    },
    {
      id: 'infinity-brain',
      route: '/infinity-brain',
      logo: infinityBrainConfig.logo,
      name: t('products.infinityBrain.name', 'Infinity Brain Training'),
      subtitle: t('products.infinityBrain.subtitle', 'Activate your brain'),
      description: (() => {
        const paragraphs = t('products.infinityBrain.aboutParagraphs', { returnObjects: true });
        if (Array.isArray(paragraphs) && paragraphs[0]) {
          const text = paragraphs[0].replace(/<[^>]*>/g, '');
          return text.length > 150 ? text.substring(0, 150) + '...' : text;
        }
        return 'Infinity Brain Training is designed to keep your brain sharp and active every day.';
      })(),
      features: (() => {
        const featuresResult = t('products.infinityBrain.features', { returnObjects: true });
        return Array.isArray(featuresResult) 
          ? (featuresResult as Array<{ emoji: string; title: string; description: string }>).slice(0, 4)
          : [];
      })(),
      gradientColors: infinityBrainConfig.gradientColors,
      playStoreUrl: infinityBrainConfig.playStoreUrl,
    },
    {
      id: 'personal-prize',
      route: '/personal-prize',
      logo: personalPrizeConfig.logo,
      name: t('products.personalPrize.name', 'Personal Prize'),
      subtitle: t('products.personalPrize.subtitle', 'Create Meaningful Surprises'),
      description: (() => {
        const paragraphs = t('products.personalPrize.aboutParagraphs', { returnObjects: true });
        if (Array.isArray(paragraphs) && paragraphs[0]) {
          const text = paragraphs[0].replace(/<[^>]*>/g, '');
          return text.length > 150 ? text.substring(0, 150) + '...' : text;
        }
        return 'Personal Prize combines a gacha simulator with an interactive reward system.';
      })(),
      features: (() => {
        const featuresResult = t('products.personalPrize.features', { returnObjects: true });
        return Array.isArray(featuresResult) 
          ? (featuresResult as Array<{ emoji: string; title: string; description: string }>).slice(0, 4)
          : [];
      })(),
      gradientColors: personalPrizeConfig.gradientColors,
      playStoreUrl: personalPrizeConfig.playStoreUrl,
      appStoreUrl: personalPrizeConfig.appStoreUrl,
      webAppUrl: personalPrizeConfig.webAppUrl,
    },
    {
      id: 'personal-vault',
      route: '/personal-vault',
      logo: personalVaultConfig.logo,
      name: t('products.personalVault.name', 'Personal Vault'),
      subtitle: t('products.personalVault.subtitle', 'Record your items and browse anytime — diaries, collections, travel & more'),
      description: (() => {
        const paragraphs = t('products.personalVault.aboutParagraphs', { returnObjects: true });
        if (Array.isArray(paragraphs) && paragraphs[0]) {
          const text = paragraphs[0].replace(/<[^>]*>/g, '');
          return text.length > 150 ? text.substring(0, 150) + '...' : text;
        }
        return 'Personal Vault: Item Tracker is a powerful personal collection management application that allows everyone to create records of their items and browse them anytime.';
      })(),
      features: (() => {
        const featuresResult = t('products.personalVault.features', { returnObjects: true });
        return Array.isArray(featuresResult) 
          ? (featuresResult as Array<{ emoji: string; title: string; description: string }>).slice(0, 4)
          : [];
      })(),
      gradientColors: personalVaultConfig.gradientColors,
      playStoreUrl: personalVaultConfig.playStoreUrl,
      appStoreUrl: personalVaultConfig.appStoreUrl,
      webAppUrl: personalVaultConfig.webAppUrl,
    },
    {
      id: 'personal-contact',
      route: '/personal-contact',
      logo: personalContactConfig.logo,
      name: t('products.personalContact.name', 'Personal Contact'),
      subtitle: t('products.personalContact.subtitle', 'Keep context with the people who matter'),
      description: (() => {
        const paragraphs = t('products.personalContact.aboutParagraphs', { returnObjects: true });
        if (Array.isArray(paragraphs) && paragraphs[0]) {
          const text = paragraphs[0].replace(/<[^>]*>/g, '');
          return text.length > 150 ? text.substring(0, 150) + '...' : text;
        }
        return 'Personal Contact is a privacy-first app for your personal contacts.';
      })(),
      features: (() => {
        const featuresResult = t('products.personalContact.features', { returnObjects: true });
        return Array.isArray(featuresResult)
          ? (featuresResult as Array<{ emoji: string; title: string; description: string }>).slice(0, 4)
          : [];
      })(),
      gradientColors: personalContactConfig.gradientColors,
      playStoreUrl: personalContactConfig.playStoreUrl,
      appStoreUrl: personalContactConfig.appStoreUrl,
    },
    {
      id: 'wayhome',
      route: '/wayhome',
      logo: wayHomeConfig.logo,
      name: t('products.wayHome.name', 'Way Home'),
      subtitle: t('products.wayHome.subtitle', 'Inspiring Puzzle'),
      description: (() => {
        const paragraphs = t('products.wayHome.aboutParagraphs', { returnObjects: true });
        if (Array.isArray(paragraphs) && paragraphs[0]) {
          const text = paragraphs[0].replace(/<[^>]*>/g, '');
          return text.length > 150 ? text.substring(0, 150) + '...' : text;
        }
        return 'Way Home takes you on a challenging puzzle-solving journey.';
      })(),
      features: (() => {
        const featuresResult = t('products.wayHome.features', { returnObjects: true });
        return Array.isArray(featuresResult) 
          ? (featuresResult as Array<{ emoji: string; title: string; description: string }>).slice(0, 4)
          : [];
      })(),
      gradientColors: wayHomeConfig.gradientColors,
      playStoreUrl: wayHomeConfig.playStoreUrl,
    },
  ];

  return (
    <div className="products-page">
      <section className="page-header">
        <div className="container">
          <h1 className="page-title">{t('products.title')}</h1>
          <p className="page-subtitle">{t('products.subtitle')}</p>
        </div>
      </section>

      <section className="products-content">
        <div className="container">
          <div className="products-grid">
            {products.map((product) => (
              <div key={product.id} className="product-card">
                <div className="product-card-header">
                  {'logo' in product && product.logo && (
                    <div className="product-logo-container">
                      <img
                        src={product.logo}
                        alt={product.name}
                        className="product-logo"
                      />
                    </div>
                  )}
                  <h2 className="product-name-white">{product.name}</h2>
                  <p className="product-subtitle-white">{product.subtitle}</p>
                </div>

                <div className="product-body">
                  <p className="product-description">{product.description}</p>

                  {product.features.length > 0 && (
                    <div className="product-features-container">
                      <div className="product-features-header">
                        <div className="product-features-icon">✨</div>
                        <h3 className="product-features-title">
                          {t('product.mainFeatures', 'Features')}
                        </h3>
                      </div>
                      <div className="product-features-list">
                        {product.features.map((feature, index) => (
                          <div key={index} className="product-feature-item">
                            <span className="product-feature-emoji" role="img" aria-label={feature.title}>
                              {feature.emoji || '⭐'}
                            </span>
                            <div>
                              <div className="product-feature-title">{feature.title}</div>
                              <div className="product-feature-desc">{feature.description}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="product-actions">
                    <Link 
                      to={product.route}
                      className="btn btn-primary btn-large"
                    >
                      {t('product.learnMore', 'Learn More')}
                    </Link>
                    {'comingSoonPlayStore' in product && product.comingSoonPlayStore ? (
                      <>
                        {(() => {
                          const label = t('product.viewOnPlayStore', 'View on Google Play');
                          const id = `${product.id}-play`;
                          return (
                            <button
                              type="button"
                              className={`btn btn-secondary btn-large store-coming-soon${comingSoonLink === id ? ' is-active' : ''}`}
                              onClick={() => showComingSoon(id)}
                              aria-label={`${label} – ${t('footer.comingSoon')}`}
                              title={t('footer.comingSoon') as string}
                            >
                              {label}
                              {comingSoonLink === id && (
                                <span className="store-coming-soon-tooltip">{t('footer.comingSoon')}</span>
                              )}
                            </button>
                          );
                        })()}
                        {'appStoreUrl' in product && product.appStoreUrl && (
                          <a
                            href={product.appStoreUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary btn-large"
                          >
                            {t('product.viewOnAppStore', 'View on App Store')}
                          </a>
                        )}
                      </>
                    ) : 'comingSoonStores' in product && product.comingSoonStores ? (
                      <>
                        {['play', 'appstore', 'web'].map((key) => {
                          const label =
                            key === 'play'
                              ? t('product.viewOnPlayStore', 'View on Google Play')
                              : key === 'appstore'
                                ? t('product.viewOnAppStore', 'View on App Store')
                                : t('product.tryOnWeb', 'Try it on Web');
                          const id = `${product.id}-${key}`;
                          return (
                            <button
                              key={id}
                              type="button"
                              className={`btn btn-secondary btn-large store-coming-soon${comingSoonLink === id ? ' is-active' : ''}`}
                              onClick={() => showComingSoon(id)}
                              aria-label={`${label} – ${t('footer.comingSoon')}`}
                              title={t('footer.comingSoon') as string}
                            >
                              {label}
                              {comingSoonLink === id && (
                                <span className="store-coming-soon-tooltip">{t('footer.comingSoon')}</span>
                              )}
                            </button>
                          );
                        })}
                      </>
                    ) : (
                      <>
                        {'playStoreUrl' in product && product.playStoreUrl && (
                          <a
                            href={product.playStoreUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary btn-large"
                          >
                            {t('product.viewOnPlayStore', 'View on Google Play')}
                          </a>
                        )}
                        {'appStoreUrl' in product && product.appStoreUrl && (
                          <a
                            href={product.appStoreUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-secondary btn-large"
                          >
                            {t('product.viewOnAppStore', 'View on App Store')}
                          </a>
                        )}
                        {'webAppUrl' in product && product.webAppUrl && (
                          <a
                            href={product.webAppUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-accent btn-large"
                          >
                            {t('product.tryOnWeb', 'Try it on Web')}
                          </a>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProductsPage;

